import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

// Base URL for the backend API for CRM records
const API_URL = 'https://onecrmapi.oneaihuman.com/api/pbcrm-records';
// Base URL for image related APIs (generation, regeneration, deletion, download)
const IMAGE_API_URL = 'https://onecrmapi.oneaihuman.com/api/pbimg';
// Base URL for blog related APIs (generation, regeneration, download)
const BLOG_API_URL = 'https://onecrmapi.oneaihuman.com/api/pbblog';
// Base URL for trends APIs
const TRENDS_API_URL = 'https://onecrmapi.oneaihuman.com/api/pbtrends';

const AllInOne = () => {
  // State for managing CRM records (topic, audience, linkword, etc.)
  const [records, setRecords] = useState([]);
  // State to track which CRM record row is currently being edited
  const [editingRowId, setEditingRowId] = useState(null);
  // State to hold the data of the CRM record being edited
  const [editedData, setEditedData] = useState({});
  // State to hold the data for a new CRM record being added via the "Add New" row
  const [newRecordData, setNewRecordData] = useState({
    topic: '',
    audience: '',
    linkword: '',
    linkurl: '',
    keywords: '',
    featuredImage: '',
    WP_SITE_URL: '',
    WP_USERNAME: '',
    WP_APP_PASSWORD: '',
    // Removed: focusKeyphrase, metaTitle, metaDescription, category, tags as they're now AI-generated
  });

  // --- States for Image Generation Feature ---
  const [excelFile, setExcelFile] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [generationErrors, setGenerationErrors] = useState([]);
  const [selectedImageSize, setSelectedImageSize] = useState('default');
  const [selectedImageExtension, setSelectedImageExtension] = useState('png');

  // --- States for Blog Generation Feature ---
  const [blogExcelFile, setBlogExcelFile] = useState(null);
  const [generatedBlogs, setGeneratedBlogs] = useState([]);
  const [isGeneratingBlogs, setIsGeneratingBlogs] = useState(false);
  const [blogGenerationErrors, setBlogGenerationErrors] = useState([]);

  // --- State for sequential generation process ---
  const [isSendingToGenerators, setIsSendingToGenerators] = useState(false);
  const [sequentialGenerationStatus, setSequentialGenerationStatus] = useState('');
  const [currentGeneratorStep, setCurrentGeneratorStep] = useState(0);

  // --- States for Google Trends Feature ---
  const [showTrendsModal, setShowTrendsModal] = useState(false);
  const [fetchedTrendsData, setFetchedTrendsData] = useState(null);
  const [isFetchingTrends, setIsFetchingTrends] = useState(false);
  const [trendsError, setTrendsError] = useState('');

  // State for autofill loading
  const [isAutofilling, setIsAutofilling] = useState(false);


  // Fetch all CRM records from the backend when the component mounts
  useEffect(() => {
    fetchRecords();
  }, []);

  // --- CRM Record Management Handlers ---

  const fetchRecords = async () => {
    try {
      const response = await axios.get(API_URL);
      setRecords(response.data);
    } catch (error) {
      console.error('Error fetching records:', error);
      alert('Failed to load records. Please check if the backend server is running.');
    }
  };

  const handleNewRecordChange = (e) => {
    const { name, value } = e.target;
    setNewRecordData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddNewRecord = async () => {
    // Basic validation for core fields
    if (!newRecordData.topic.trim() || !newRecordData.audience.trim() || !newRecordData.linkword.trim() ||
        !newRecordData.linkurl.trim() || !newRecordData.keywords.trim() || !newRecordData.featuredImage.trim()) {
      alert('Topic, Audience, Link Word, Link URL, Keywords, and Featured Image are required for a new record!');
      return;
    }
    try {
      const response = await axios.post(API_URL, newRecordData);
      setRecords([...records, response.data]);
      setNewRecordData({
        topic: '', audience: '', linkword: '', linkurl: '', keywords: '', featuredImage: '',
        WP_SITE_URL: '', WP_USERNAME: '', WP_APP_PASSWORD: '',
      });
    } catch (error) {
      console.error('Error adding new record:', error);
      alert('Failed to add new record. Please try again.');
    }
  };

  const handleEditClick = (record) => {
    setEditingRowId(record._id);
    setEditedData({ ...record });
  };

  const handleEditedDataChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSaveClick = async (id) => {
    // Basic validation for core fields
    if (!editedData.topic.trim() || !editedData.audience.trim() || !editedData.linkword.trim() ||
        !editedData.linkurl.trim() || !editedData.keywords.trim() || !editedData.featuredImage.trim()) {
      alert('Topic, Audience, Link Word, Link URL, Keywords, and Featured Image cannot be empty!');
      return;
    }
    try {
      const response = await axios.put(`${API_URL}/${id}`, editedData);
      setRecords(records.map(rec => (rec._id === id ? response.data : rec)));
      setEditingRowId(null);
      setEditedData({});
    } catch (error) {
      console.error('Error updating record:', error);
      alert('Failed to update record. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditedData({});
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/${id}`);
      setRecords(records.filter(rec => rec._id !== id));
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Failed to delete record. Please try again.');
    }
  };

  // Function to generate and download the CRM data as an Excel file
  const generateCrmExcel = () => {
    if (records.length === 0) {
      alert('No CRM data to export. Please add some data first.');
      return;
    }

    const headers = [
      "topic",
      "audience",
      "linkword",
      "linkurl",
      "keywords",
      "featuredImage",
      "WP_SITE_URL",
      "WP_USERNAME",
      "WP_APP_PASSWORD",
      "created at"
    ];

    const dataToExport = records.map(row => [
      row.topic,
      row.audience,
      row.linkword,
      row.linkurl,
      row.keywords,
      row.featuredImage,
      row.WP_SITE_URL,
      row.WP_USERNAME,
      row.WP_APP_PASSWORD,
      new Date(row.createdAt).toLocaleDateString(),
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataToExport]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Content CRM Data");
    XLSX.writeFile(wb, "Content_CRM_Data.xlsx");
  };

  // --- Reusable Helper Functions for Generators ---

  const _generateImagesFromFile = async (file, format) => {
    const formData = new FormData();
    formData.append('excelFile', file);
    try {
      const response = await axios.post(`${IMAGE_API_URL}/generate-images?format=${format}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { generated: response.data.generatedImages, errors: response.data.errors };
    } catch (error) {
      console.error('Error during image generation:', error);
      throw new Error(error.response?.data?.message || error.message || 'Unknown error during image generation.');
    }
  };

  const _generateBlogsFromFile = async (file) => {
    const formData = new FormData();
    formData.append('excelFile', file);
    try {
      const response = await axios.post(`${BLOG_API_URL}/generate-blogs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return { generated: response.data.generatedBlogs, errors: response.data.errors };
    } catch (error) {
      console.error('Error during blog generation:', error);
      throw new Error(error.response?.data?.message || error.message || 'Unknown error during blog generation.');
    }
  };

  // --- Image Generation Handlers ---

  const handleFileChange = (e) => {
    setExcelFile(e.target.files[0]);
  };

  const handleGenerateImages = async () => {
    if (!excelFile) {
      alert('Please select an Excel/CSV file first.');
      return;
    }
    setIsGeneratingImages(true);
    setGenerationErrors([]);
    setGeneratedImages([]);
    try {
      const { generated, errors } = await _generateImagesFromFile(excelFile, selectedImageExtension);
      setGeneratedImages(generated);
      setGenerationErrors(errors);
      alert('Image generation complete! Check the section below.');
    } catch (error) {
      alert(`Failed to generate images: ${error.message}`);
      setGenerationErrors([{ error: error.message }]);
    } finally {
      setIsGeneratingImages(false);
      setExcelFile(null);
      document.getElementById('excelFileInput').value = '';
    }
  };

  const handleRegenerateImage = async (imageInfo, originalFeaturedImage) => {
    if (!window.confirm(`Regenerate image for topic: ${imageInfo.topic}?`)) {
      return;
    }
    setIsGeneratingImages(true);
    try {
      const singleRecordData = [[ "topic", "featuredImage" ], [ imageInfo.topic, originalFeaturedImage ]];
      const ws = XLSX.utils.aoa_to_sheet(singleRecordData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RegenImage");
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const tempFile = new File([excelBlob], `regen_image_${imageInfo.topic}.xlsx`, { type: 'application/octet-stream' });

      const { generated } = await _generateImagesFromFile(tempFile, selectedImageExtension);
      if (generated.length > 0) {
        setGeneratedImages(prevImages =>
          prevImages.map(img =>
            img.topic === imageInfo.topic ? generated[0] : img
          )
        );
        alert('Image regenerated successfully!');
      } else {
        alert('Image regeneration failed: No image generated.');
      }
    } catch (error) {
      console.error('Error regenerating image:', error);
      alert(`Failed to regenerate image: ${error.message}`);
    } finally {
      setIsGeneratingImages(false);
    }
  };

  const handleDeleteImage = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete the image: ${filename}?`)) {
      return;
    }
    try {
      await axios.delete(`${IMAGE_API_URL}/delete-image/${filename}`);
      setGeneratedImages(prevImages =>
        prevImages.filter(img => img.filename !== filename)
      );
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Check console for details.');
    }
  };

  // --- Blog Generation Handlers ---

  const handleBlogFileChange = (e) => {
    setBlogExcelFile(e.target.files[0]);
  };

  const handleGenerateBlogs = async () => {
    if (!blogExcelFile) {
      alert('Please select an Excel/CSV file for blog generation first.');
      return;
    }
    setIsGeneratingBlogs(true);
    setBlogGenerationErrors([]);
    setGeneratedBlogs([]);
    try {
      const { generated, errors } = await _generateBlogsFromFile(blogExcelFile);
      setGeneratedBlogs(generated);
      setBlogGenerationErrors(errors);
      alert('Blog generation complete! Check the section below.');
    } catch (error) {
      alert(`Failed to generate blogs: ${error.message}`);
      setBlogGenerationErrors([{ error: error.message }]);
    } finally {
      setIsGeneratingBlogs(false);
      setBlogExcelFile(null);
      document.getElementById('blogExcelFileInput').value = '';
    }
  };

  const handleRegenerateBlog = async (blogInfo) => {
    if (!window.confirm(`Regenerate blog for topic: "${blogInfo.topic}"?`)) {
      return;
    }
    setIsGeneratingBlogs(true);
    try {
      // Prepare data with all necessary content fields for regeneration
      const singleRecordData = [
        [ "topic", "audience", "linkword", "linkurl", "keywords", "featuredImage",
          "WP_SITE_URL", "WP_USERNAME", "WP_APP_PASSWORD" ],
        [ blogInfo.topic, blogInfo.audience, blogInfo.linkword, blogInfo.linkurl, blogInfo.keywords, blogInfo.featuredImage,
          blogInfo.WP_SITE_URL, blogInfo.WP_USERNAME, blogInfo.WP_APP_PASSWORD
        ]
      ];
      const ws = XLSX.utils.aoa_to_sheet(singleRecordData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "RegenBlog");
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const tempFile = new File([excelBlob], `regen_blog_${blogInfo.topic}.xlsx`, { type: 'application/octet-stream' });

      const { generated } = await _generateBlogsFromFile(tempFile);
      if (generated.length > 0) {
        const newBlogEntry = { ...generated[0] };
        if (newBlogEntry.uploadStatus?.success && newBlogEntry.uploadStatus?.message) {
          const match = newBlogEntry.uploadStatus.message.match(/(https?:\/\/[^\s]+)/);
          if (match && match[1]) {
            newBlogEntry.wpLink = match[1];
          }
        }

        setGeneratedBlogs(prevBlogs =>
          prevBlogs.map(blog =>
            blog.topic === blogInfo.topic ? newBlogEntry : blog
          )
        );
        alert('Blog regenerated successfully!');
      } else {
        alert('Blog regeneration failed: No blog generated.');
      }
    } catch (error) {
      console.error('Error regenerating blog:', error);
      alert(`Failed to regenerate blog: ${error.message}`);
    } finally {
      setIsGeneratingBlogs(false);
    }
  };

  // --- Sequential Generation Handler (triggered by "Send to Generators" button) ---
  const handleSendToGenerators = async (record) => {
    // Check for all required content fields (metadata will be generated by AI)
    if (!record.topic || !record.featuredImage || !record.audience || !record.linkword || !record.linkurl || !record.keywords) {
      alert('Topic, Audience, Link Word, Link URL, Keywords, and Featured Image are all required to send to generators. Please fill these in.');
      return;
    }
    if (record.featuredImage.split(' ').length < 3) {
      alert('For better image generation results, please provide a more descriptive "Featured Image" prompt (e.g., "A detailed illustration of X in Y style" instead of just "X").');
    }


    setIsSendingToGenerators(true);
    setSequentialGenerationStatus('Starting sequential generation...');
    setCurrentGeneratorStep(0);
    setGenerationErrors([]);
    setBlogGenerationErrors([]);

    try {
      // 1. Prepare data for Image Generator
      setSequentialGenerationStatus(`Step 1/2: Generating image for "${record.topic}"...`);
      setCurrentGeneratorStep(1);
      const imageExcelData = [[ "topic", "featuredImage" ], [ record.topic, record.featuredImage ]];
      const imageWs = XLSX.utils.aoa_to_sheet(imageExcelData);
      const imageWb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(imageWb, imageWs, "SingleImage");
      const imageExcelBuffer = XLSX.write(imageWb, { bookType: 'xlsx', type: 'array' });
      const imageExcelBlob = new Blob([imageExcelBuffer], { type: 'application/octet-stream' });
      const imageTempFile = new File([imageExcelBlob], `single_image_${record.topic}.xlsx`, { type: 'application/octet-stream' });

      const { generated: imgGenerated, errors: imgErrors } = await _generateImagesFromFile(imageTempFile, selectedImageExtension);
      if (imgGenerated.length > 0) {
        setGeneratedImages(prev => {
          const existingIndex = prev.findIndex(img => img.topic === record.topic);
          if (existingIndex > -1) {
            const newArr = [...prev];
            newArr[existingIndex] = imgGenerated[0];
            return newArr;
          }
          return [...prev, imgGenerated[0]];
        });
        setSequentialGenerationStatus(`Step 1/2: Image for "${record.topic}" generated successfully.`);
      } else if (imgErrors.length > 0) {
        setSequentialGenerationStatus(`Step 1/2: Image generation failed for "${record.topic}".`);
        setGenerationErrors(prev => [...prev, ...imgErrors]);
        throw new Error('Image generation failed.');
      } else {
        setSequentialGenerationStatus(`Step 1/2: Image generation yielded no results for "${record.topic}".`);
        throw new Error('Image generation yielded no results.');
      }

      // 2. Prepare data for Blog Generator (now only content fields are passed)
      setSequentialGenerationStatus(`Step 2/2: Generating blog for "${record.topic}" and attempting upload...`);
      setCurrentGeneratorStep(2);
      const blogExcelData = [
        [ "topic", "audience", "linkword", "linkurl", "keywords", "featuredImage",
          "WP_SITE_URL", "WP_USERNAME", "WP_APP_PASSWORD" ],
        [ record.topic, record.audience, record.linkword, record.linkurl, record.keywords, record.featuredImage,
          record.WP_SITE_URL, record.WP_USERNAME, record.WP_APP_PASSWORD
        ]
      ];
      const blogWs = XLSX.utils.aoa_to_sheet(blogExcelData);
      const blogWb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(blogWb, blogWs, "SingleBlog");
      const excelBuffer = XLSX.write(blogWb, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const blogTempFile = new File([excelBlob], `single_blog_${record.topic}.xlsx`, { type: 'application/octet-stream' });

      const { generated: blogGenerated, errors: blogErrors } = await _generateBlogsFromFile(blogTempFile);
      if (blogGenerated.length > 0) {
        const newBlogEntry = { ...blogGenerated[0] };
        if (newBlogEntry.uploadStatus?.success && newBlogEntry.uploadStatus?.message) {
          const match = newBlogEntry.uploadStatus.message.match(/(https?:\/\/[^\s]+)/);
          if (match && match[1]) {
            newBlogEntry.wpLink = match[1];
          }
        }

        setGeneratedBlogs(prev => {
          const existingIndex = prev.findIndex(blog => blog.topic === record.topic);
          if (existingIndex > -1) {
            const newArr = [...prev];
            newArr[existingIndex] = newBlogEntry;
            return newArr;
          }
          return [...prev, newBlogEntry];
        });
        setSequentialGenerationStatus(`Step 2/2: Blog for "${record.topic}" generated and uploaded. All done!`);
        setCurrentGeneratorStep(3);
      } else if (blogErrors.length > 0) {
        setSequentialGenerationStatus(`Step 2/2: Blog generation failed for "${record.topic}".`);
        setBlogGenerationErrors(prev => [...prev, ...blogErrors]);
      } else {
        setSequentialGenerationStatus(`Step 2/2: Blog generation yielded no results for "${record.topic}".`);
      }

    } catch (error) {
      console.error('Error in sequential generation:', error);
      setSequentialGenerationStatus(`Sequential generation failed: ${error.message}`);
      setGenerationErrors(prev => [...prev, { error: `General sequential generation error: ${error.message}` }]);
    } finally {
      setIsSendingToGenerators(false);
      setTimeout(() => setCurrentGeneratorStep(0), 1500);
    }
  };

  // --- Google Trends Handlers ---

  const handleFetchDailyTrends = async () => {
    setShowTrendsModal(true);
    setFetchedTrendsData(null);
    setTrendsError('');
    setIsFetchingTrends(true);

    try {
      const response = await axios.get(`${TRENDS_API_URL}/keyword?topic=${encodeURIComponent('current events')}&geo=IN&timeframe=today 1-m`);
      setFetchedTrendsData(response.data);
    } catch (error) {
      console.error('Error fetching monthly trends:', error);
      if (error.response?.data?.error && error.response.data.error.includes('Google returned a response with code 404')) {
        setTrendsError("Monthly trending topics are currently unavailable for this region or for the generic search term. Please try again later or select another region.");
      } else {
        setTrendsError(error.response?.data?.message || error.message || 'Failed to fetch monthly trends.');
      }
    } finally {
      setIsFetchingTrends(false);
    }
  };

  const handlePopulateTopicFromTrend = (trendQuery) => {
    if (editingRowId) {
      setEditedData(prevEditedData => ({
        ...prevEditedData,
        topic: trendQuery
      }));
      setRecords(prevRecords => prevRecords.map(rec =>
        rec._id === editingRowId
          ? { ...rec, topic: trendQuery }
          : rec
      ));
    } else {
      setNewRecordData(prevNewRecordData => ({
        ...prevNewRecordData,
        topic: trendQuery
      }));
    }
    closeTrendsModal();
  };

  const closeTrendsModal = () => {
    setShowTrendsModal(false);
    setFetchedTrendsData(null);
    setTrendsError('');
  };

  // Handle Autofill button click
  const handleAutofillClick = async (recordId, currentTopic, currentFeaturedImage, isNewRecord) => {
    setIsAutofilling(true);
    try {
      const initialData = {
        initialTopic: currentTopic || '',
        initialFeaturedImage: currentFeaturedImage || '',
      };

      const response = await axios.post(`${API_URL}/autofill`, initialData);
      const autofilledData = response.data;

      const updateData = {
        topic: autofilledData.topic,
        audience: autofilledData.audience,
        linkword: autofilledData.linkWord,
        linkurl: autofilledData.linkUrl,
        keywords: autofilledData.keywords,
        featuredImage: autofilledData.featuredImage,
        WP_SITE_URL: autofilledData.WP_SITE_URL,
        WP_USERNAME: autofilledData.WP_USERNAME,
        WP_APP_PASSWORD: autofilledData.WP_APP_PASSWORD,
        // Autofilled new metadata fields are not returned by the backend anymore
      };

      if (isNewRecord) {
        setNewRecordData(prevData => ({
          ...prevData,
          ...updateData,
        }));
      } else {
        setEditedData(prevData => ({
          ...prevData,
          ...updateData,
        }));
      }
      alert('Autofill successful!');
    } catch (error) {
      console.error('Error during autofill:', error);
      alert(`Autofill failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsAutofilling(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-10 px-4 font-inter">
      {/* Tailwind CSS CDN and Google Fonts for Inter */}
      <script src="https://cdn.tailwindcss.com"></script>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"></link>

      {/* Custom CSS styles for Excel-like appearance and generation sections */}
      <style>
        {`
          body { font-family: 'Inter', sans-serif; }
          /* Excel table styling */
          .excel-table {
            width: 100%;
            border-collapse: collapse;
          }
          .excel-table th, .excel-table td {
            border: 1px solid #e2e8f0;
            padding: 8px 12px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 180px; /* Default max-width for most cells */
          }
          /* Specific styling for the 'Actions' column to make buttons visible */
          .actions-column {
            max-width: 300px; /* Increased max-width for actions column */
            min-width: 200px; /* Ensure it doesn't shrink too much */
            width: auto; /* Allow it to expand if content needs */
          }
          .excel-table th.actions-column {
            text-align: center; /* Center header text for actions */
          }
          .excel-table td.actions-column {
            /* Apply flexbox to the cell content to allow wrapping and spacing */
            display: flex;
            flex-direction: column; /* Force vertical stacking */
            flex-wrap: wrap; /* Allow buttons to wrap to next line */
            gap: 0.5rem; /* Space between buttons */
            justify-content: center; /* Center buttons horizontally */
            align-items: center; /* Center buttons vertically */
            white-space: normal; /* Allow text/buttons to wrap within the cell */
            overflow: visible; /* Ensure buttons are not cut off */
            text-overflow: clip; /* No ellipsis for this cell */
          }


          .excel-table th {
            background-color: #f0f0f0;
            font-weight: 600;
            color: #333;
            text-align: left;
          }
          .excel-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .excel-input {
            border: 1px solid #cbd5e0;
            padding: 4px 8px;
            border-radius: 4px;
            box-shadow: inset 0 1px 2px rgba(0,0,0,0.05);
            width: 100%;
            box-sizing: border-box;
          }
          /* Action button styling */
          .action-button {
            padding: 6px 10px;
            border-radius: 5px;
            font-size: 0.875rem;
            font-weight: 500;
            transition: background-color 0.15s ease-in-out;
            cursor: pointer;
          }

          .edit-button { background-color: #3b82f6; color: white; }
          .edit-button:hover { background-color: #2563eb; }
          .save-button { background-color: #22c55e; color: white; }
          .save-button:hover { background-color: #16a34a; }
          .delete-button { background-color: #ef4444; color: white; }
          .delete-button:hover { background-color: #dc2626; }
          .cancel-button { background-color: #6b7280; color: white; }
          .cancel-button:hover { background-color: #4b5563; }
          .download-button { background-color: #0ea5e9; color: white; }
          .download-button:hover { background-color: #0284c7; }
          .blog-button { background-color: #f97316; color: white; } /* orange-500 */
          .blog-button:hover { background-color: #ea580c; } /* orange-600 */
          .send-to-gen-button { background-color: #8b5cf6; color: white; } /* violet-500 */
          .send-to-gen-button:hover { background-color: #7c3aed; } /* violet-600 */
          .fetch-trends-button { background-color: #10b981; color: white; } /* emerald-500 */
          .fetch-trends-button:hover { background-color: #059669; } /* emerald-600 */
          .autofill-button { background-color: #6366f1; color: white; } /* indigo-500 */
          .autofill-button:hover { background-color: #4f46e5; } /* indigo-600 */


          /* Styling for the "Add New Record" row */
          .new-record-row td {
            background-color: #e0f2fe;
            border-top: 2px solid #93c5fd;
          }
          .new-record-row .excel-input {
            background-color: #ffffff;
          }

          /* Image generation specific styles */
          .image-grid, .blog-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 1rem;
            margin-top: 1.5rem;
          }
          .image-card, .blog-card {
            background-color: white;
            border: 1px solid #e2e8f0;
            border-radius: 0.5rem;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            display: flex;
            flex-direction: column;
          }
          .image-card img {
            width: 100%;
            height: 150px;
            object-fit: cover;
            border-bottom: 1px solid #e2e8f0;
          }
          .image-card-content, .blog-card-content {
            padding: 0.75rem;
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          .image-card-title, .blog-card-title {
            font-weight: 600;
            color: #333;
            margin-bottom: 0.5rem;
            word-break: break-all;
          }
          .image-card-actions, .blog-card-actions {
            display: flex;
            flex-wrap: wrap; /* Allow buttons to wrap */
            gap: 0.5rem;
            margin-top: 0.75rem;
          }
          .image-card-actions button, .image-card-actions a,
          .blog-card-actions button, .blog-card-actions a {
            flex: 1;
            padding: 0.5rem;
            font-size: 0.8rem;
          }

          /* Progress Indicator Styles */
          .generator-progress-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin: 2rem 0; /* Space above and below */
          }
          .progress-dot {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background-color: #cbd5e0; /* Gray for inactive */
            transition: background-color 0.3s ease-in-out;
            border: 2px solid #cbd5e0;
          }
          .progress-line {
            width: 2px; /* Thin vertical line */
            height: 30px; /* Length of the line */
            background-color: #cbd5e0; /* Gray for inactive */
            transition: background-color 0.3s ease-in-out;
          }
          /* Active/Completed states */
          .progress-dot.active, .progress-line.active {
            background-color: #22c55e; /* Green for active */
            border-color: #22c55e;
          }
          .progress-dot.completed, .progress-line.completed {
            background-color: #16a34a; /* Darker green for completed */
            border-color: #16a34a;
          }

          /* Modal Styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.7);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background-color: white;
            padding: 2rem;
            border-radius: 0.75rem;
            box-shadow: 0 10px 15px rgba(0, 0, 0, 0.2);
            width: 90%;
            max-width: 800px; /* Increased max-width for horizontal trends */
            max-height: 80vh;
            overflow-y: auto;
            position: relative;
          }
          .modal-close-button {
            position: absolute;
            top: 1rem;
            right: 1rem;
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #6b7280;
          }
          .modal-close-button:hover {
            color: #374151;
          }
          .trend-item {
            cursor: pointer;
            padding: 8px 12px; /* More padding for chips */
            border: 1px solid #cbd5e0;
            border-radius: 9999px; /* Fully rounded for chip effect */
            background-color: #f0f4f8; /* Light background for chips */
            transition: background-color 0.1s ease-in-out, border-color 0.1s ease-in-out;
            font-size: 0.9rem;
            white-space: nowrap; /* Prevent wrapping inside chip */
          }
          .trend-item:hover {
            background-color: #e0f2fe; /* Light blue on hover */
            border-color: #93c5fd;
          }
          .trends-horizontal-grid {
            display: flex; /* Use flex for horizontal layout */
            flex-wrap: wrap; /* Allow wrapping to next line */
            gap: 0.75rem; /* Space between trend items */
            justify-content: center; /* Center items horizontally */
            margin-top: 1rem;
          }
        `}
      </style>

      {/* Main Application Container */}
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-7xl">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Content Management System</h1>

        {/* Global Fetch Trends Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={handleFetchDailyTrends}
            className="px-8 py-4 bg-emerald-600 text-white font-extrabold text-lg rounded-md shadow-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition duration-150 ease-in-out transform hover:scale-105"
            disabled={isFetchingTrends}
          >
            {isFetchingTrends ? 'Fetching Trending Topics...' : 'Fetch Trending Topics (Last Month)'}
          </button>
        </div>

        {/* CRM Records Table Section */}
        <h2 className="text-xl font-semibold mb-4 text-center text-gray-800">All Content Records</h2>
        <div className="overflow-x-auto rounded-lg shadow-md border border-gray-300 mb-6">
          <table className="excel-table">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topic</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Audience</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link Word</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keywords</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Featured Image</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WP Site URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WP Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WP App Password</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider actions-column">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {records.length > 0 ? (
                records.map(record => (
                  <tr key={record._id}>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="text" name="topic" value={editedData.topic || ''} onChange={handleEditedDataChange} className="excel-input" />
                      ) : (
                        record.topic || '-'
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="text" name="audience" value={editedData.audience || ''} onChange={handleEditedDataChange} className="excel-input" />
                      ) : (
                        record.audience || '-'
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="text" name="linkword" value={editedData.linkword || ''} onChange={handleEditedDataChange} className="excel-input" />
                      ) : (
                        record.linkword || '-'
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="url" name="linkurl" value={editedData.linkurl || ''} onChange={handleEditedDataChange} placeholder="https://example.com" className="excel-input" />
                      ) : (
                        <a href={record.linkurl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{record.linkurl || '-'}</a>
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="text" name="keywords" value={editedData.keywords || ''} onChange={handleEditedDataChange} className="excel-input" />
                      ) : (
                        record.keywords || '-'
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <textarea name="featuredImage" value={editedData.featuredImage || ''} onChange={handleEditedDataChange} rows="1" className="excel-input resize-y"></textarea>
                      ) : (
                        record.featuredImage || '-'
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="url" name="WP_SITE_URL" value={editedData.WP_SITE_URL || ''} onChange={handleEditedDataChange} placeholder="https://your-site.com/wp-json/wp/v2" className="excel-input" />
                      ) : (
                        record.WP_SITE_URL || '-'
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="text" name="WP_USERNAME" value={editedData.WP_USERNAME || ''} onChange={handleEditedDataChange} className="excel-input" />
                      ) : (
                        record.WP_USERNAME || '-'
                      )}
                    </td>
                    <td>
                      {editingRowId === record._id ? (
                        <input type="password" name="WP_APP_PASSWORD" value={editedData.WP_APP_PASSWORD || ''} onChange={handleEditedDataChange} className="excel-input" />
                      ) : (
                        record.WP_APP_PASSWORD ? '********' : '-'
                      )}
                    </td>
                    <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                    <td className="actions-column">
                      {editingRowId === record._id ? (
                        <>
                          <button onClick={() => handleSaveClick(record._id)} className="action-button save-button">Save</button>
                          <button onClick={handleCancelEdit} className="action-button cancel-button">Cancel</button>
                          <button
                            onClick={() => handleAutofillClick(record._id, editedData.topic, editedData.featuredImage, false)}
                            className="action-button autofill-button mt-1"
                            disabled={isAutofilling}
                          >
                            {isAutofilling ? 'Autofilling...' : 'Autofill'}
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleEditClick(record)} className="action-button edit-button">Edit</button>
                          <button onClick={() => handleDeleteClick(record._id)} className="action-button delete-button">Delete</button>
                          <button
                            onClick={() => handleSendToGenerators(record)}
                            className="action-button send-to-gen-button"
                            disabled={isSendingToGenerators || !record.featuredImage || !record.audience || !record.linkword || !record.linkurl || !record.keywords}
                          >
                            {isSendingToGenerators ? 'Sending...' : 'Send to Generators'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="px-6 py-4 text-center text-gray-500">No records found. Add a new row below!</td> {/* Adjusted colspan */}
                </tr>
              )}
              {/* --- New Record Input Row --- */}
              <tr className="new-record-row">
                <td>
                  <input type="text" name="topic" value={newRecordData.topic} onChange={handleNewRecordChange} placeholder="New Topic" className="excel-input" />
                </td>
                <td>
                  <input type="text" name="audience" value={newRecordData.audience} onChange={handleNewRecordChange} placeholder="New Audience" className="excel-input" />
                </td>
                <td>
                  <input type="text" name="linkword" value={newRecordData.linkword} onChange={handleNewRecordChange} placeholder="New Link Word" className="excel-input" />
                </td>
                <td>
                  <input type="url" name="linkurl" value={newRecordData.linkurl} onChange={handleNewRecordChange} placeholder="https://new.link" className="excel-input" />
                </td>
                <td>
                  <input type="text" name="keywords" value={newRecordData.keywords} onChange={handleNewRecordChange} placeholder="New Keywords" className="excel-input" />
                </td>
                <td>
                  <textarea name="featuredImage" value={newRecordData.featuredImage} onChange={handleNewRecordChange} rows="1" placeholder="New Featured Image Prompt" className="excel-input resize-y"></textarea>
                </td>
                <td>
                  <input type="url" name="WP_SITE_URL" value={newRecordData.WP_SITE_URL} onChange={handleNewRecordChange} placeholder="https://your-site.com/wp-json/wp/v2" className="excel-input" />
                </td>
                <td>
                  <input type="text" name="WP_USERNAME" value={newRecordData.WP_USERNAME} onChange={handleNewRecordChange} placeholder="username" className="excel-input" />
                </td>
                <td>
                  <input type="password" name="WP_APP_PASSWORD" value={newRecordData.WP_APP_PASSWORD} onChange={handleNewRecordChange} placeholder="app password" className="excel-input" />
                </td>
                <td></td>
                <td>
                  <button onClick={handleAddNewRecord} className="action-button save-button w-full">Add New</button>
                  <button
                    onClick={() => handleAutofillClick(null, newRecordData.topic, newRecordData.featuredImage, true)}
                    className="action-button autofill-button w-full mt-1"
                    disabled={isAutofilling}
                  >
                    {isAutofilling ? 'Autofilling...' : 'Autofill New'}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SECURITY WARNING */}
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative mb-8" role="alert">
          <strong className="font-bold">Security Warning:</strong>
          <span className="block sm:inline"> Storing "Site App Password" directly in the database and Excel, and transmitting it in plain text is **highly insecure**. For production, consider using OAuth, environment variables for API keys, or a secure secrets management solution.</span>
        </div>

        {/* Sequential Generation Status Display (moved here for better flow) */}
        {isSendingToGenerators && (
          <div className="text-center mb-4">
            <div className="text-violet-600 font-medium mb-2">{sequentialGenerationStatus}</div>
          </div>
        )}

        {/* Progress Indicator for Image Generator */}
        <div className="generator-progress-container">
          <div className={`progress-dot ${currentGeneratorStep >= 1 ? 'active' : ''}`}></div>
          <div className={`progress-line ${currentGeneratorStep >= 1 ? 'active' : ''}`}></div>
        </div>

        {/* --- Image Generation Section --- */}
        <div className="border border-gray-300 p-6 rounded-xl shadow-lg bg-gray-50 mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Image Generator (from Excel/CSV Prompts)</h2>

          {/* Image Options (Size & Extension) - UI and functional for extension */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <label htmlFor="imageSize" className="text-gray-700 text-sm font-medium">Image Size:</label>
              <select
                id="imageSize"
                value={selectedImageSize}
                onChange={(e) => setSelectedImageSize(e.target.value)}
                className="excel-input py-2 px-3"
              >
                <option value="default">Default (Model Defined)</option>
                <option value="small">Small (Placeholder)</option>
                <option value="medium">Medium (Placeholder)</option>
                <option value="large">Large (Placeholder)</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="imageExtension" className="text-gray-700 text-sm font-medium">Image Format:</label>
              <select
                id="imageExtension"
                value={selectedImageExtension}
                onChange={(e) => setSelectedImageExtension(e.target.value)}
                className="excel-input py-2 px-3"
              >
                <option value="png">PNG</option>
                <option value="jpeg">JPG</option>
              </select>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600 mb-6">
            **Note:** Gemini 2.5 Flash image generation API currently outputs images at a model-defined size. Size selection here is for future enhancements or would require additional server-side/client-side image processing. Image format conversion (PNG/JPG) is now supported.
          </p>


          {/* File Upload and Generate Button */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-6">
            <input
              type="file"
              id="excelFileInput"
              accept=".xlsx, .xls, .csv"
              onChange={handleFileChange}
              className="block w-full md:w-auto text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <button
              onClick={handleGenerateImages}
              disabled={!excelFile || isGeneratingImages}
              className={`px-8 py-3 rounded-md shadow-md font-semibold transition duration-150 ease-in-out ${
                !excelFile || isGeneratingImages
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
              }`}
            >
              {isGeneratingImages ? 'Generating Images...' : 'Generate Images from Excel/CSV'}
            </button>
          </div>

          {/* Loading Indicator */}
          {isGeneratingImages && (
            <div className="text-center text-indigo-600 font-medium mb-4">
              Please wait, generating images. This may take a while for large files...
            </div>
          )}

          {/* Generation Errors Display */}
          {generationErrors.length > 0 && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Generation Errors:</strong>
              <ul className="mt-2 list-disc list-inside">
                {generationErrors.map((err, index) => (
                  <li key={index}>
                    {err.topic ? `Topic: "${err.topic}" - ` : ''}
                    {err.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Generated Images Display */}
          {generatedImages.length > 0 && (
            <>
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Generated Images</h3>
              <div className="image-grid">
                {generatedImages.map((image) => {
                  // Find the original record using image.topic to get the correct featuredImage for regeneration
                  const originalRecord = records.find(rec => rec.topic === image.topic);
                  const originalFeaturedImage = originalRecord ? originalRecord.featuredImage : 'Featured Image prompt not found in CRM records.';

                  return (
                    <div key={image.filename} className="image-card">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`}
                        alt={image.topic}
                        className="rounded-t-md"
                        onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x150/e0e0e0/555555?text=Image+Error"; }}
                      />
                      <div className="image-card-content">
                        <div className="image-card-title">{image.topic}</div>
                        <div className="image-card-actions">
                          <button
                            onClick={() => handleRegenerateImage(image, originalFeaturedImage)}
                            className="action-button save-button"
                            disabled={isGeneratingImages}
                          >
                            Regenerate
                          </button>
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/download-image/${image.filename}`}
                            className="action-button download-button text-center no-underline flex items-center justify-center"
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={isGeneratingImages}
                          >
                            Download
                          </a>
                          <button
                            onClick={() => handleDeleteImage(image.filename)}
                            className="action-button delete-button"
                            disabled={isGeneratingImages}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div> {/* End Image Generation Section */}

        {/* Progress Indicator for Blog Generator */}
        <div className="generator-progress-container">
          <div className={`progress-dot ${currentGeneratorStep >= 2 ? 'active' : ''}`}></div>
          <div className={`progress-line ${currentGeneratorStep >= 2 ? 'active' : ''}`}></div>
        </div>

        {/* --- Blog Generation Section --- */}
        <div className="border border-gray-300 p-6 rounded-xl shadow-lg bg-gray-50 mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Blog Generator (from Excel/CSV Topics)</h2>

          {/* File Upload and Generate Button for Blogs */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-6">
            <input
              type="file"
              id="blogExcelFileInput"
              accept=".xlsx, .xls, .csv"
              onChange={handleBlogFileChange}
              className="block w-full md:w-auto text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-white focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
            />
            <button
              onClick={handleGenerateBlogs}
              disabled={!blogExcelFile || isGeneratingBlogs}
              className={`px-8 py-3 rounded-md shadow-md font-semibold transition duration-150 ease-in-out ${
                !blogExcelFile || isGeneratingBlogs
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-orange-600 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2'
              }`}
            >
              {isGeneratingBlogs ? 'Generating Blogs...' : 'Generate Blogs from Excel/CSV'}
            </button>
          </div>

          {/* Loading Indicator for Blogs */}
          {isGeneratingBlogs && (
            <div className="text-center text-orange-600 font-medium mb-4">
              Please wait, generating blogs. This may take a while for large files...
            </div>
          )}

          {/* Blog Generation Errors Display */}
          {blogGenerationErrors.length > 0 && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Blog Generation Errors:</strong>
              <ul className="mt-2 list-disc list-inside">
                {blogGenerationErrors.map((err, index) => (
                  <li key={index}>
                    {err.topic ? `Topic: "${err.topic}" - ` : ''}
                    {err.error}
                    {err.uploadStatus && !err.uploadStatus.success && ` (Upload: ${err.uploadStatus.message})`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Generated Blogs Display */}
          {generatedBlogs.length > 0 && (
            <>
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Generated Blogs</h3>
              <div className="blog-grid">
                {generatedBlogs.map((blog) => (
                  <div key={blog.jsonFilename} className="blog-card"> {/* Changed key to jsonFilename */}
                    <div className="blog-card-content">
                      <div className="blog-card-title">{blog.topic}</div>
                      {blog.uploadStatus && (
                        <p className={`text-xs font-semibold ${blog.uploadStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                          Upload Status: {blog.uploadStatus.message}
                        </p>
                      )}
                      <div className="blog-card-actions">
                        {blog.wpLink && (
                          <a
                            href={blog.wpLink}
                            className="action-button blog-button text-center no-underline flex items-center justify-center"
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={isGeneratingBlogs}
                          >
                            Open Blog
                          </a>
                        )}
                        <button
                          onClick={() => handleRegenerateBlog(blog)}
                          className="action-button blog-button"
                          disabled={isGeneratingBlogs}
                        >
                          Regenerate
                        </button>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/download-blog/${blog.jsonFilename}`} 
                          className="action-button download-button text-center no-underline flex items-center justify-center"
                          target="_blank"
                          rel="noopener noreferrer"
                          download={`${blog.topic}.doc`} 
                          disabled={isGeneratingBlogs}
                        >
                          Download (DOC)
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div> {/* End Blog Generation Section */}
      </div>

      {/* Google Trends Modal */}
      {showTrendsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button onClick={closeTrendsModal} className="modal-close-button">&times;</button>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Trending Topics (Last Month - India)</h2>

            {isFetchingTrends ? (
              <div className="text-center text-blue-600 py-4">Fetching trends...</div>
            ) : trendsError ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                <strong className="font-bold">Error:</strong> {trendsError}
              </div>
            ) : fetchedTrendsData && (fetchedTrendsData.top_related_queries?.length > 0 || fetchedTrendsData.rising_related_queries?.length > 0) ? (
              <div>
                <p className="text-center text-gray-700 mb-4">
                  Click on a trend to populate the "Topic" field of the active record or new record row.
                </p>
                {fetchedTrendsData.top_related_queries && fetchedTrendsData.top_related_queries.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">Top Related Queries:</h3>
                    <div className="trends-horizontal-grid">
                      {fetchedTrendsData.top_related_queries.slice(0, 10).map((trend, index) => (
                        <div
                          key={`top-${index}`}
                          className="trend-item"
                          onClick={() => handlePopulateTopicFromTrend(trend.query)}
                        >
                          {trend.query}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {fetchedTrendsData.rising_related_queries && fetchedTrendsData.rising_related_queries.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">Rising Related Queries:</h3>
                    <div className="trends-horizontal-grid">
                      {fetchedTrendsData.rising_related_queries.slice(0, 10).map((trend, index) => (
                        <div
                          key={`rising-${index}`}
                          className="trend-item"
                          onClick={() => handlePopulateTopicFromTrend(trend.query)}
                        >
                          {trend.query}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-4 mt-6">
                  <button
                    onClick={closeTrendsModal}
                    className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md shadow-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-600 py-4">No trending topics found for the last month in this region based on the generic search. This may be due to data limitations from the external Google Trends service.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AllInOne;
